import { fetchAgencyBillingConfig, createAgencyBillingPortalSession } from "@/services/agency-billing.service";

const emptyStatus = () => ({
  paymentFailed: false,
  paymentWarning: false,
  accessLocked: false,
  graceEndsAt: null,
  paymentMessage: null,
  hostedInvoiceUrl: null,
  subscriptionStatus: null,
});

export const agencyBilling = {
  namespaced: true,
  state: {
    ...emptyStatus(),
    loaded: false,
    loading: false,
    lastError: null,
  },
  getters: {
    showPaymentWarning: (state) => state.paymentWarning && !state.accessLocked,
    showPaymentLock: (state) => state.accessLocked,
    paymentBannerMessage: (state) =>
      state.paymentMessage ||
      (state.accessLocked
        ? "Your platform subscription payment failed and the grace period has ended. Update your payment method to restore access."
        : "Your platform subscription payment failed. Please update your payment method."),
  },
  actions: {
    async refresh({ commit, dispatch, rootGetters, state }, options = {}) {
      const force = options?.force === true;

      let role = rootGetters["profile/role"];
      if (!role) {
        try {
          await dispatch("profile/me", { force: true }, { root: true });
        } catch (error) {
          // Not authenticated / profile unavailable — nothing to show.
          commit("CLEAR");
          return null;
        }
        role = rootGetters["profile/role"];
      }

      if (role !== "agency_admin") {
        commit("CLEAR");
        return null;
      }

      if (state.loading && !force) {
        return null;
      }

      commit("SET_LOADING", true);
      try {
        const config = await fetchAgencyBillingConfig();
        const platform = config.platform || {};
        commit("SET_STATUS", {
          paymentFailed: Boolean(platform.payment_failed),
          paymentWarning: Boolean(platform.payment_warning),
          accessLocked: Boolean(platform.access_locked),
          graceEndsAt: platform.grace_ends_at || null,
          paymentMessage: platform.payment_message || null,
          hostedInvoiceUrl: platform.hosted_invoice_url || null,
          subscriptionStatus: platform.subscription_status || null,
        });
        return config;
      } catch (error) {
        commit("SET_ERROR", error?.response?.data?.message || error?.message || "Failed to load billing status");
        if (force) {
          throw error;
        }
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async openStripePortal({ commit }) {
      try {
        const { url } = await createAgencyBillingPortalSession();
        if (url) {
          window.location.href = url;
          return;
        }
        throw new Error("No Stripe portal URL returned");
      } catch (error) {
        commit("SET_ERROR", error?.response?.data?.message || error?.message || "Unable to open payment portal");
        throw error;
      }
    },
    clear({ commit }) {
      commit("CLEAR");
    },
  },
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_STATUS(state, status) {
      Object.assign(state, emptyStatus(), status, { loaded: true, lastError: null });
    },
    SET_ERROR(state, message) {
      state.lastError = message;
      state.loaded = true;
    },
    CLEAR(state) {
      Object.assign(state, emptyStatus(), { loaded: false, loading: false, lastError: null });
    },
  },
};
